/**
 * Shared rank / percentile helpers used across the IQ visualization stack
 * (charts, tables, metric cards).
 *
 * Conventions:
 *   - Rank is 1-indexed and ascending by $/MW (rank 1 = cheapest).
 *   - Competition ranking ("1224"): tied values share the same rank, and
 *     the next distinct value's rank skips the tied positions.
 *   - Percentile uses "strictly less than" semantics: the percentile for a
 *     given project is the share of `total` that is strictly cheaper than
 *     it, so all tied-cheapest ($0) projects share the same percentile.
 *     Output is floored at P1 and capped at P100.
 */

/**
 * Map a "count of projects STRICTLY cheaper than this one" to a P1..P100
 * percentile: `round((numStrictlyLess / total) * 100)`, floored at P1.
 *
 * This is intentionally a strictly-less-than percentile, NOT `rank / total`,
 * so that ties anchor to the LOWEST shared percentile and the next distinct
 * (more expensive) value jumps past the entire tied block. Worked example —
 * 100 projects with 32 of them tied at $0:
 *   - all 32 cheapest projects have 0 strictly cheaper  -> P1 (floored), and
 *   - the 33rd project (32 strictly cheaper)            -> jumps to P32.
 * This matches the IQ rule that every $0 / tied-cheapest project reads as
 * "best in the pool" instead of being spread across P1..Pn.
 *
 * Consequence (BY DESIGN, not a bug): the single most-expensive project does
 * not reach P100 for typical pool sizes. With `total = N` and no ties at the
 * top it tops out near `round((N-1)/N * 100)` — e.g. P50 in a 2-project pool,
 * P90 in 10, P99 in 100. The scale measures "share of the pool strictly
 * cheaper than you", not a 0..100 rank position. Do NOT "fix" this to
 * `rank / total`: that would scatter the tied-cheapest cohort the convention
 * deliberately keeps together. See `rankFormatting.test.ts` for the pinned
 * tie behavior.
 */
export const computePercentile = (
    numStrictlyLess: number,
    total: number
): number =>
    total > 0
        ? Math.max(
              1,
              Math.min(100, Math.round((numStrictlyLess / total) * 100))
          )
        : 1;

/** Render rank with percentile as the focus: "p14 (28 of 100)". */
export const formatRank = (
    rank: number,
    total: number,
    percentile: number
): string => `p${percentile} (${rank} of ${total})`;

/**
 * Given a list of numeric values (e.g. cost_per_mw across projects), return
 * a function that, given a 0-based index into the SORTED-ASCENDING array,
 * returns the percentile for that index using strictly-less-than logic.
 *
 * Implementation tracks the first index at which each unique value appears,
 * so all items sharing a value get the same percentile.
 */
export const buildPercentileLookup = (
    sortedValuesAsc: ReadonlyArray<number>
): ((indexInSorted: number) => number) => {
    const firstIdxByValue: number[] = new Array(sortedValuesAsc.length);
    let firstIdx = 0;
    let prev: number | undefined;
    sortedValuesAsc.forEach((v, i) => {
        if (prev === undefined || v !== prev) {
            firstIdx = i;
            prev = v;
        }
        firstIdxByValue[i] = firstIdx;
    });
    const total = sortedValuesAsc.length;
    return (indexInSorted: number) =>
        computePercentile(
            firstIdxByValue[indexInSorted] ?? indexInSorted,
            total
        );
};

export interface ProjectRankings {
    /** Project ID -> 1-indexed rank using competition ranking. */
    rankById: { [projectId: string]: number };
    /** Project ID -> P1..P100 percentile. */
    percentileById: { [projectId: string]: number };
    /** Total number of ranked projects. */
    total: number;
}

/**
 * Coerce a possibly-null / undefined / non-finite cost value to the
 * canonical "$0" sentinel used by the ranker. NaN, +Infinity, and
 * -Infinity break numeric sort + tie detection downstream, so we treat
 * them the same as null / undefined: tied with the cheapest cohort at
 * rank 1 / P1.
 */
const sanitizeCost = (v: number | null | undefined): number =>
    typeof v === "number" && Number.isFinite(v) ? v : 0;

/**
 * Rank a collection of project rows by $/MW ascending.
 *
 * - `getId(row)` returns the project ID.
 * - `getCostPerMw(row)` returns the project's $/MW. Null, undefined, and
 *   non-finite values (NaN, ±Infinity) are all treated as $0 so they
 *   share rank 1 / P1 with other tied-cheapest rows. This matches the
 *   IQ portfolio rule that $0 projects show alongside the cheapest
 *   cohort instead of being silently dropped, and defends against the
 *   non-finite values that occasionally leak through from Hasura before
 *   reaching the converter layer.
 *
 * Returns a `ProjectRankings` with `rankById`, `percentileById`, and
 * `total`. Callers that need to exclude rows without a valid $/MW from
 * the ranking pool should filter before passing them in.
 */
export const rankProjectsByCostPerMw = <T>(
    rows: ReadonlyArray<T>,
    getId: (row: T) => string,
    getCostPerMw: (row: T) => number | null | undefined
): ProjectRankings => {
    const sorted = rows.slice().sort((a, b) => {
        const va = sanitizeCost(getCostPerMw(a));
        const vb = sanitizeCost(getCostPerMw(b));
        return va - vb;
    });
    const total = sorted.length;
    const rankById: { [projectId: string]: number } = {};
    const percentileById: { [projectId: string]: number } = {};
    let firstIdx = 0;
    let prevValue: number | undefined;
    sorted.forEach((p, i) => {
        const v = sanitizeCost(getCostPerMw(p));
        if (prevValue === undefined || v !== prevValue) {
            firstIdx = i;
            prevValue = v;
        }
        const id = getId(p);
        rankById[id] = firstIdx + 1; // competition ranking
        percentileById[id] = computePercentile(firstIdx, total);
    });
    return { rankById, percentileById, total };
};
